// ---------------------------------------------------------------------------
// AgentEditor — picks an agent that already exists in the workspace store.
// The template records the agent's name; inline authoring isn't supported.
//
// In view mode (and before the catalog loads) the referenced name renders
// read-only so saved templates still surface it.
// ---------------------------------------------------------------------------

import {
  Body1,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
} from '@fluentui/react-components';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import type {
  AgentDraft,
  AgentSummary,
} from '../../EvaluationTemplateForm.types.js';

export interface AgentEditorProps {
  agent: AgentDraft;
  /** Existing agents in the workspace — populates the picker. */
  agents?: AgentSummary[];
  agentsLoading?: boolean;
  onUpdate: (patch: Partial<AgentDraft>) => void;
}

export function AgentEditor({
  agent,
  agents = [],
  agentsLoading = false,
  onUpdate,
}: AgentEditorProps) {
  const styles = useStyles();

  return (
    <Field
      label="Agent"
      required
      hint="Pick an agent already created in this workspace."
    >
      {agentsLoading ? (
        <Spinner size="tiny" label="Loading agents..." />
      ) : agents.length === 0 ? (
        // Catalog empty: either view mode (not fetched) or a workspace with
        // no agents. If a name is already referenced (seeded from a saved
        // template), show it read-only so view/edit still surface it.
        agent.name ? (
          <Input value={agent.name} disabled aria-label="Agent" />
        ) : (
          <Body1 className={styles.helperText}>
            No agents in this workspace yet. Create an agent via the API first.
          </Body1>
        )
      ) : (
        <Dropdown
          aria-label="Agent"
          placeholder="Choose an agent"
          value={
            agents.find((a) => a.name === agent.name)?.displayName ?? agent.name
          }
          selectedOptions={agent.name ? [agent.name] : []}
          onOptionSelect={(_e, data) => {
            if (data.optionValue) {
              onUpdate({ name: data.optionValue, version: undefined });
            }
          }}
        >
          {agents.map((a) => {
            const label = a.displayName ?? a.name;
            return (
              <Option key={a.name} value={a.name} text={label}>
                {label}
              </Option>
            );
          })}
        </Dropdown>
      )}
    </Field>
  );
}
